import type { CityConfig } from "@/pages/city-template/types";

export const lebanonOrConfig: CityConfig = {
  city: "Lebanon",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "lebanon-or",
  route: "/lebanon-or",
  title: "Emergency Passport Lebanon OR | Expedited Passport Linn County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Lebanon, OR — as fast as 24 hours. Fast Passport Center serves Lebanon, Sweet Home, Albany, and the mid-Willamette Valley. Registered State Dept. courier. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Lebanon, Oregon",
  heroSubtitle: "Lebanon resident with an urgent passport need? Rural Linn County's passport resources are limited to the Lebanon Post Office and Albany facilities. The nearest regional agency is in Seattle — a 4-hour drive north. Fast Passport Center gets Lebanon residents expedited passports in days, entirely remotely. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lebanon%20Oregon%20rural%20town%20nestled%20in%20the%20Willamette%20Valley%20with%20Cascade%20Mountain%20foothills%20visible%20in%20the%20background%2C%20Santiam%20River%20corridor%20running%20through%20green%20agricultural%20landscape%2C%20small%20town%20Main%20Street%20with%20historic%20brick%20buildings%2C%20Pacific%20Northwest%20rural%20community%20charm%20with%20golden%20late%20afternoon%20light%2C%20lush%20green%20farmland%20and%20timber%20country%2C%20photorealistic%20no%20text%20no%20people&width=1800&height=700&seq=lebanon-or-hero-01&orientation=landscape",
  heroImageAlt: "Lebanon Oregon in the mid-Willamette Valley with Cascade Mountain foothills and Santiam River corridor",
  geoPosition: "44.5365;-122.9070",
  geoPlacename: "Lebanon, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 260 miles north — approximately 4 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Lebanon", zips: "97355" },
    { name: "Sweet Home", zips: "97386" },
    { name: "Albany", zips: "97321–97322" },
    { name: "Jefferson", zips: "97352" },
    { name: "Scio", zips: "97374" },
    { name: "Millersburg", zips: "97321" },
    { name: "Tangent", zips: "97389" },
    { name: "Halsey", zips: "97348" },
    { name: "Corvallis", zips: "97330–97333" },
    { name: "Salem", zips: "97301–97306" },
    { name: "Harrisburg", zips: "97446" },
    { name: "Junction City", zips: "97448" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Lebanon", desc: "Complete your emergency or expedited passport application from Lebanon — no drive to Albany, Salem, or Seattle required." },
    { icon: "ri-truck-line", title: "Ship Overnight from Albany or Corvallis", desc: "FedEx and UPS locations in Albany (10 miles west) and Corvallis serve the mid-Willamette Valley for overnight passport document shipping." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — especially important for rural Lebanon residents far from passport resources." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We submit through reserved Seattle Passport Agency channels — bypassing the public appointment system entirely." },
  ],
  testimonials: [
    {
      name: "Jennifer D.",
      location: "Lebanon, OR",
      rating: 5,
      text: "I had a family cruise to Alaska that stopped in Vancouver. Discovered my passport was expired two weeks before departure. Fast Passport Center renewed it in 5 days. I shipped from the Albany UPS and had it back before I needed to leave.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Robert K.",
      location: "Sweet Home, OR",
      rating: 5,
      text: "First-time passport for a Mexico trip from Lebanon. The Lebanon Post Office had no appointments for two weeks. Fast Passport Center guided me through the Albany County Clerk step and expedited it from there. Passport back in 9 days.",
      service: "First-Time Passport",
    },
    {
      name: "Linda M.",
      location: "Lebanon, OR",
      rating: 5,
      text: "My daughter needed a passport for an OSU study abroad program to Spain. The deadline was coming fast. Fast Passport Center handled the child passport application and got it back in 7 days. Saved her semester abroad.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "lebanon-post-main",
      name: "Lebanon Post Office",
      type: "USPS Passport Acceptance",
      address: "445 S 5th St",
      city: "Lebanon",
      state: "OR",
      zip: "97355",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Lebanon — primary passport acceptance for Lebanon and Sweet Home residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance for Lebanon, Sweet Home, and east Linn County residents. Appointment required — limited availability, so book early. Albany or Corvallis are recommended for urgent applications.",
      icon: "ri-mail-line",
    },
    {
      id: "albany-post-main",
      name: "Albany Main Post Office",
      type: "USPS Passport Acceptance",
      address: "200 W 2nd Ave",
      city: "Albany",
      state: "OR",
      zip: "97321",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Albany — about 10 miles west via US-20",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Best option for Lebanon residents when the Lebanon Post Office has no availability. Well-staffed downtown Albany post office.",
      icon: "ri-mail-line",
    },
    {
      id: "albany-usps-2",
      name: "Albany NE Post Office",
      type: "USPS Passport Acceptance",
      address: "2680 Santiam Hwy SE",
      city: "Albany",
      state: "OR",
      zip: "97322",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "SE Albany — about 10 miles west from Lebanon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "SE Albany post office — alternate for Lebanon residents when the main Albany post office is booked.",
      icon: "ri-mail-line",
    },
    {
      id: "corvallis-post-main",
      name: "Corvallis Main Post Office",
      type: "USPS Passport Acceptance",
      address: "311 SW 2nd St",
      city: "Corvallis",
      state: "OR",
      zip: "97333",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 10am–2pm",
      distance: "Corvallis — about 25 miles west via US-20",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Corvallis Main Post Office has Saturday morning passport hours and better availability than any post office in Linn County. Recommended for Lebanon residents with urgent timelines.",
      icon: "ri-mail-line",
    },
    {
      id: "linn-county-clerk",
      name: "Linn County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "300 SW 4th Ave",
      city: "Albany",
      state: "OR",
      zip: "97321",
      phone: "(541) 967-3831",
      hours: "Mon–Fri 8am–5pm",
      distance: "Albany — about 10 miles west, Linn County Courthouse",
      website: "https://www.co.linn.or.us/clerk/",
      websiteLabel: "Linn County Clerk",
      notes: "Linn County Clerk in Albany accepts passport applications. Often has better availability than Lebanon Post Office.",
      icon: "ri-government-line",
    },
    {
      id: "benton-county-clerk",
      name: "Benton County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "120 NW 4th St",
      city: "Corvallis",
      state: "OR",
      zip: "97330",
      phone: "(541) 766-6800",
      hours: "Mon–Fri 8am–5pm",
      distance: "Corvallis — about 25 miles west via US-20",
      website: "https://www.co.benton.or.us/clerk",
      websiteLabel: "Benton County Clerk",
      notes: "Benton County Clerk in Corvallis — excellent option for Lebanon residents with a car, offering good appointment availability.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Lebanon, Oregon?",
      a: "Yes. Fast Passport Center provides emergency passport processing for Lebanon residents — as fast as 24 hours for urgent travel. The nearest regional passport agency is in Seattle, 4 hours north. We serve Lebanon entirely remotely: start online, ship overnight from Albany or Corvallis, and we handle agency submission.",
    },
    {
      q: "How fast can I renew my passport from Lebanon, OR?",
      a: "Adult passport renewal from Lebanon is available in as little as 3 business days through Fast Passport Center. Most adult renewals are by mail — no in-person appointment required. Ship overnight from Albany or Corvallis FedEx/UPS.",
    },
    {
      q: "Where is the best passport acceptance office near Lebanon, Oregon?",
      a: "Lebanon has the Lebanon Post Office (445 S 5th St) as the primary local acceptance facility. For better availability, Albany Main Post Office (10 miles west) or Corvallis Main Post Office (25 miles west) are recommended. The Corvallis location has Saturday morning hours.",
    },
    {
      q: "Does Lebanon, Oregon have a passport agency?",
      a: "No. Lebanon has one post office that accepts passport applications. The nearest regional passport agency is in Seattle, 4 hours away. Fast Passport Center bypasses the need for a Seattle trip through reserved agency submission.",
    },
    {
      q: "Can I expedite a passport for a Canada trip from Lebanon?",
      a: "Yes. Canada is the most common international destination for Lebanon area residents — Vancouver BC, Whistler, and Victoria are within a day's drive. Fast Passport Center handles expedited passport renewals in 3–9 business days.",
    },
    {
      q: "How long does standard passport processing take from Lebanon, OR?",
      a: "Standard USPS mail processing from Lebanon runs 6–13 weeks, and longer during peak travel season. If your trip is within 10 weeks, expedited service through Fast Passport Center is strongly recommended.",
    },
    {
      q: "Can I get a passport without visiting an office in Lebanon?",
      a: "Yes — most adult Lebanon residents renewing a passport can do so entirely by mail. Start online with Fast Passport Center, ship overnight from Albany or Corvallis, and we handle the rest. No visit to any processing office required.",
    },
    {
      q: "Can Lebanon students get expedited passports?",
      a: "Yes. Fast Passport Center regularly helps students from Lebanon, Sweet Home, and east Linn County get expedited passports for study abroad, international volunteer programs, and academic travel. Start as soon as your program is confirmed.",
    },
    {
      q: "What airport do Lebanon residents use for international travel?",
      a: "Lebanon residents typically drive to Portland International Airport (PDX, about 75 miles north via I-5) for international flights. Eugene Airport (about 35 miles south) is closer for regional connections. Salem Airport is about 20 miles north.",
    },
    {
      q: "Can I get a passport photo in Lebanon, Oregon?",
      a: "Lebanon has drugstore photo services at Walgreens and Rite Aid. For professional passport photos, Albany and Corvallis have more options including Walmart, FedEx Office, and Costco. Fast Passport Center provides photo specifications to ensure government acceptance.",
    },
  ],
  photoLocations: [
    { name: "Rite Aid — Lebanon", address: "1420 S Main St, Lebanon, OR 97355", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Albany", address: "3400 Killdeer Ave SE, Albany, OR 97322", hours: "Daily 7 AM – 11 PM" },
    { name: "FedEx Office — Albany", address: "2200 14th Ave SE, Albany, OR 97322", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Walgreens — Albany Geary", address: "2750 Geary St SE, Albany, OR 97322", hours: "Daily 8 AM – 10 PM" },
    { name: "FedEx Office — Corvallis", address: "343 SW 2nd St, Corvallis, OR 97333", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Walmart — Corvallis", address: "444 NW Elks Dr, Corvallis, OR 97330", hours: "Daily 7 AM – 11 PM" },
    { name: "Costco Photo Center — Salem", address: "3795 Commercial St SE, Salem, OR 97302", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "Walgreens — Salem Lancaster", address: "1840 Lancaster Dr NE, Salem, OR 97305", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Eugene", address: "4550 W 11th Ave, Eugene, OR 97402", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Eugene", address: "1740 W 11th Ave, Eugene, OR 97402", hours: "Daily 8 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Lebanon Driver & Motor Vehicle Services",
      address: "1460 S Main St, Lebanon, OR 97355",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Lebanon DMV — driver licenses, REAL ID, and ID cards for Lebanon and east Linn County residents.",
      featured: true,
    },
    {
      name: "Albany Driver & Motor Vehicle Services",
      address: "1700 Geary St SE, Albany, OR 97322",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Albany DMV — larger Linn County office with good capacity for Albany, Lebanon, and Sweet Home area residents.",
      featured: false,
    },
    {
      name: "Sweet Home Driver & Motor Vehicle Services",
      address: "1470 12th Ave, Sweet Home, OR 97386",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Sweet Home DMV — serves Sweet Home and the Santiam Canyon area.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary hub — about 75 miles north via I-5",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport is the primary international departure hub for Lebanon area residents. PDX offers direct service to Amsterdam, London, Tokyo, Reykjavik, Mexico City, and Vancouver BC.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "EUG",
      name: "Eugene Airport",
      distance: "About 35 miles south via I-5",
      address: "28855 Lockheed Dr, Eugene, OR 97402",
      note: "Eugene Airport provides regional connections for Lebanon residents heading to international departures. Closer to Lebanon than PDX.",
      flightUrl: "https://www.eugene-or.gov/airport",
      flightLabel: "Eugene Airport Website",
      featured: false,
    },
    {
      code: "SLE",
      name: "Salem Airport",
      distance: "About 20 miles north via I-5",
      address: "2890 25th St SE, Salem, OR 97302",
      note: "Salem Airport has limited commercial service — most Lebanon residents use PDX for international flights.",
      flightUrl: "https://salemail.com/",
      flightLabel: "Salem Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Lebanon & Sweet Home",
      description: "Last-minute international trip or family emergency? Fast Passport Center handles emergency passport processing for Lebanon residents — as fast as 24 hours. No Seattle road trip required.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal — Lebanon, OR",
      description: "Passport expired? Lebanon residents renew fast through Fast Passport Center — ship overnight from Albany or Corvallis, and we handle the expedited agency submission.",
      bullets: ["Fastest renewal option for Lebanon residents", "Ship overnight from Albany or Corvallis", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost or stolen passport in Lebanon or before a trip? We guide east Linn County travelers through DS-64 and expedited replacement — entirely remotely.",
      bullets: ["DS-64 reporting guidance", "Expedited replacement timelines", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Lebanon Families",
      description: "Lebanon families traveling to Canada, Mexico, or internationally need child passports. We guide you through the Lebanon or Albany acceptance step — you handle the visit, we expedite from there.",
      bullets: ["Both parents typically required to consent", "Lebanon Post Office or Albany guidance", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First-time passport from Lebanon? We guide you through the Lebanon Post Office or Albany/Linn County Clerk step and handle expedited processing after.",
      bullets: ["Step-by-step acceptance guidance", "Photo location guide for Lebanon area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — submitting through reserved agency channels at prices 30–100% lower than retail expediting services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Passport Services",
      description: "Name change after marriage, damaged passport, second passport — all available with expedited courier access for Lebanon area residents.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passport for frequent travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Lebanon, Oregon travelers with urgent international departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Lebanon and Linn County residents needing passports faster than standard USPS timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Lebanon, Oregon residents — ship overnight from Albany or Corvallis, we handle the rest." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Lebanon travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Lebanon and Linn County families traveling internationally." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Lebanon, Oregon and the mid-Willamette Valley area." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Assuming Rural Lebanon Has Same Passport Access as Portland", description: "Lebanon has only one post office for passport acceptance. Many rural residents wait until the last week to check, only to find no appointments available. Start your passport application at least 8–10 weeks before travel — or use expedited service if within that window." },
    { icon: "ri-camera-off-line", title: "Passport Photo Rejected by the Government", description: "Photos from small-town pharmacies often have lighting or background issues. Fast Passport Center reviews photo specifications before submission to prevent costly rejections and delays." },
    { icon: "ri-file-damage-line", title: "Missing Signatures or Form Errors", description: "One missing signature or form error causes weeks of delay. Our pre-submission review catches all errors before they reach the agency." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate or Document Format", description: "We specify the exact document format required for your state of birth and passport type — preventing the rejections that are common with first-time applicants." },
    { icon: "ri-map-pin-time-line", title: "Ignoring the Six-Month Validity Rule", description: "Canada, Mexico, and most European countries require 6 months of validity beyond your return date. A passport with 3 months left is effectively expired for most international travel." },
    { icon: "ri-calendar-close-line", title: "Not Planning for the Lebanon-to-Albany/Corvallis Drive for Acceptance", description: "Lebanon residents often need to drive to Albany or Corvallis for passport acceptance appointments. Add this drive time to your planning — first-time applicants especially need the in-person visit." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Lebanon travel date. Complete your secure order in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized checklist with Lebanon and mid-Willamette Valley area acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Albany or Corvallis", description: "Drop off at FedEx or UPS in Albany or Corvallis — both serve Lebanon with overnight shipping." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — no waiting for public appointments." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Lebanon Address", description: "Your completed passport ships directly to your Lebanon or Linn County address. Track status in real time." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 4 hours each way",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Not practical for most Lebanon residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship from Albany or Corvallis overnight",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; longer during peak season",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent or tracking",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited Seattle slots, weeks out", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (4 hours each way)", fpc: "Ship overnight from Albany or Corvallis" },
    { feature: "Processing Speed", agency: "Strict eligibility — urgent travel only", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Rural Accessibility", agency: "Not designed for Lebanon/Linn County", fpc: "Fully remote — no car trip to Seattle" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + travel cost", fpc: "Best price guarantee — no travel cost" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Albany", route: "/albany-or" },
    { name: "Emergency passport help in Corvallis", route: "/corvallis" },
    { name: "Fast passport renewal in Salem", route: "/salem" },
    { name: "Rush passport services in Eugene", route: "/eugene" },
    { name: "Expedited passports for Oregon travelers", route: "/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from Albany or Corvallis FedEx/UPS. No office visit required for most services.",
  fpcOfficeDistance: "Serving Lebanon and the entire mid-Willamette Valley area remotely",
  serviceAreaIntro: "Whether you're in Lebanon, Sweet Home, Scio, Jefferson, or anywhere in east Linn County — Fast Passport Center provides emergency and expedited passport processing without the 4-hour drive to Seattle.",
  nearbyCitiesIntro: "We serve all Linn County zip codes including Lebanon, Sweet Home, Albany, Jefferson, Scio, Halsey, Tangent, and Millersburg.",
  howItWorksIntro: "Getting an emergency or expedited passport from Lebanon is easier than a 4-hour drive to Seattle. Most east Linn County clients complete the process without visiting an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Lebanon and east Linn County passport applicants. Our review process prevents all of them.",
  testimonialsIntro: "Real reviews from Lebanon, Sweet Home, and east Linn County travelers who needed their passports fast.",
  faqIntro: "The most common questions Lebanon and east Linn County residents ask about emergency and expedited passport services.",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (Oregon Vital Records)", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/", desc: "Oregon Health Authority — Vital Records for birth and death certificates statewide" },
    { label: "Marriage Licenses (Linn County Clerk)", url: "https://www.co.linn.or.us/clerk/", desc: "Linn County Clerk for Lebanon and east Linn County" },
    { label: "Divorce Decrees (Linn County Circuit Court)", url: "https://www.courts.oregon.gov/courts/linn/", desc: "Linn County Circuit Court for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Oregon vital records online via VitalChek" },
  ],
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Lebanon and East Linn County Residents",
    intro: "Lebanon is a rural hub for east Linn County — gateway to the Santiam Canyon, the Cascade Mountains, and Oregon's agricultural interior. The city's location between Corvallis and Eugene creates an international travel pattern that includes family trips, agricultural business travel, and outdoor adventure trips to Canada and Mexico.",
    cards: [
      {
        icon: "ri-plant-line",
        title: "Agricultural & Timber Industry Travel",
        subtitle: "Linn County grass seed, timber, and agricultural equipment exports to Canada, Japan, and Europe",
        description: "Lebanon and east Linn County are part of Oregon's agricultural and timber economy. Trade shows, agricultural exchange programs, and buyer visits to Canada, Japan, South Korea, and European markets are common. These trips are often scheduled seasonally — spring for trade shows and fall for harvest-season export coordination.",
        tip: "Agricultural business trip to Canada or Japan? Check your passport when the trip is confirmed — visa requirements for Japan often require 3+ months of validity beyond the travel date.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "Student & Academic Travel from East Linn County",
        subtitle: "Lebanon and Sweet Home students attend programs throughout the Willamette Valley and beyond",
        description: "Students from Lebanon and Sweet Home attend Linn-Benton Community College, Oregon State University, and University of Oregon — and pursue study abroad programs, international volunteer service, and academic travel. Study abroad deadlines are often 4–6 months before departure.",
        tip: "If your student is accepted to a study abroad program, check their passport immediately upon acceptance. Program application deadlines are often before passport processing can complete.",
      },
      {
        icon: "ri-landscape-line",
        title: "Cascade & Outdoor Adventure Travel",
        subtitle: "Lebanon residents use the Santiam Pass for mountain access — and extend trips to BC and Alaska",
        description: "Lebanon sits at the base of the Cascade Mountains, and many residents are outdoor enthusiasts who extend local mountain trips to international destinations. Ski trips to Whistler BC, fishing trips to British Columbia, and Alaska cruises from Seattle are common extensions of the Cascade outdoor lifestyle.",
        tip: "Alaska cruises require a passport for Canadian ports of call. If you're booking a June–August cruise, check your passport in January or February — the busiest cruise booking season.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "Portland International (PDX) & Eugene Access from Lebanon",
        subtitle: "75 miles to PDX; 35 miles to Eugene Airport",
        description: "Lebanon residents use Portland International (PDX, 75 miles north) for direct international flights, and Eugene Airport (35 miles south) for regional connections. The drive to PDX is a key factor in pre-trip planning — many Lebanon residents add the 75-mile drive to their departure buffer.",
        tip: "If driving to PDX for an international flight, check your passport when booking. The PDX drive from Lebanon adds 1.5 hours to your departure schedule — factor passport processing time into your planning.",
      },
    ],
    callout: {
      icon: "ri-landscape-line",
      title: "Lebanon, Oregon — Gateway to the Santiam and the Cascades",
      description: "Lebanon sits at the crossroads of the mid-Willamette Valley and the Cascade Mountains. The rural community is connected to international travel through agricultural trade, outdoor adventure, and the academic corridor between Albany and Eugene. Fast Passport Center serves Lebanon, Sweet Home, and east Linn County with the same expedited passport expertise as Portland-area residents — without the Seattle drive.",
    },
  },
  destinations: {
    title: "Where Lebanon Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations we see most from Lebanon and east Linn County clients. The mix of rural agricultural trade, family Canada trips, and outdoor adventure creates a unique international travel profile.",
    stats: [
      { stat: "260 mi", label: "to Seattle Passport Agency — nearest regional office from Lebanon" },
      { stat: "75 mi", label: "to Portland International Airport (PDX) — primary hub for Lebanon" },
      { stat: "10 mi", label: "to Albany — closest passport acceptance city from Lebanon" },
      { stat: "2–9 Days", label: "fastest expedited passport processing available for Lebanon residents" },
    ],
    destinations: [
      { name: "British Columbia, Canada", context: "Vancouver BC, Whistler skiing, and fishing — top destination for Lebanon and east Linn County residents", urgency: "Spring/summer BC bookings; passport issues discovered during seasonal trip planning", icon: "ri-map-pin-2-line" },
      { name: "Mexico — Cabo, Puerto Vallarta, Cancun", context: "Winter and spring beach travel for Lebanon families — warm-weather escapes from November through April", urgency: "October–November Mexico bookings; passports found expired during January departure preparation", icon: "ri-sun-cloudy-line" },
      { name: "Alaska — Inside Passage Cruises", context: "Alaska cruises from Seattle are popular with Lebanon and east Linn County families — passports required for Canadian ports", urgency: "Spring cruise bookings in January; passport issues discovered at cruise check-in", icon: "ri-ship-line" },
      { name: "Costa Rica & Central America", context: "Eco-adventure travel for Linn County outdoor community — dry season (December–April) is peak travel", urgency: "September–November bookings; passport expiration discovered at booking", icon: "ri-leaf-line" },
      { name: "Japan & South Korea", context: "Agricultural trade missions and technology exchange for Linn County agricultural and timber industry", urgency: "Trade show registrations 3–4 months ahead; visa applications need valid passport", icon: "ri-global-line" },
      { name: "Europe — UK, France, Germany", context: "Heritage travel, agricultural trade shows, and university programs for Lebanon area residents", urgency: "Trade show and academic travel planned months ahead; passport issues discovered at registration", icon: "ri-building-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the Willamette Valley with the same expedited passport expertise.",
    crossLinks: [
      { name: "Albany", route: "/albany-or" },
      { name: "Corvallis", route: "/corvallis" },
      { name: "Salem", route: "/salem" },
    ],
  },
};